import { CSRFCodes, isAPIErrorResponse } from './errorHandling';
import { progressPromise } from './progressPromise';
export class RequestError extends Error {
    constructor(reason, requestOptions, response) {
        super(`Request failed ${reason}`);
        this.reason = reason;
        this.requestOptions = requestOptions;
        this.response = response;
    }
}
export class APIError extends RequestError {
    constructor(apiResponse, requestOptions, response) {
        super('API Error', requestOptions, response);
        this.apiResponse = apiResponse;
    }
}
const isoDateMatch = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
const jsonReviver = (_key, value) => {
    if (typeof value === 'string' && isoDateMatch.test(value)) {
        return new Date(value);
    }
    return value;
};
function makeQueryString(query) {
    const segments = [];
    Object.keys(query).forEach(key => {
        // tslint:disable-next-line: no-unnecessary-type-assertion
        segments.push(`${encodeURIComponent(key)}=${encodeURIComponent(query[key].toString())}`);
    });
    return `?${segments.join('&')}`;
}
export class APIClient {
    async sendFile(opts) {
        let { url } = opts;
        const { method = 'POST', progressor, payload, retry, query } = opts;
        return progressPromise((resolve, reject, progress) => {
            const methodUpper = method.toUpperCase();
            const xhr = new XMLHttpRequest();
            xhr.responseType = 'json';
            xhr.withCredentials = true;
            xhr.onerror = () => reject(new RequestError('Failed to upload file', opts, this.xhrResponseObject(xhr)));
            xhr.onabort = () => reject(new RequestError('Request aborted', opts, this.xhrResponseObject(xhr)));
            xhr.onload = () => {
                const { status, response } = xhr;
                if (status === 204) {
                    resolve(undefined);
                    return;
                }
                if (status >= 200 && status <= 299) {
                    resolve(response);
                    return;
                }
                if (isAPIErrorResponse(response)) {
                    if (this.shouldCSRFRetry(status, response) && retry !== false) {
                        resolve(this.sendFile({
                            ...opts,
                            retry: false,
                        }));
                        return;
                    }
                    reject(new APIError(response, opts, this.xhrResponseObject(xhr)));
                }
                reject(new RequestError('Failed to upload', opts, this.xhrResponseObject(xhr)));
                return;
            };
            xhr.onprogress = ev => progress(ev.loaded / ev.total);
            if (query) {
                url += makeQueryString(query);
            }
            xhr.open(methodUpper, url);
            const csrf = this.getCSRF();
            if (csrf) {
                xhr.setRequestHeader('x-csrf', csrf);
            }
            xhr.send(payload);
        }, progressor);
    }
    async listRequest(opts, page = 0) {
        if (typeof opts === 'object') {
            opts.page = page;
        }
        else {
            opts = {
                page,
                url: opts,
            };
        }
        const res = await this.requestInternal(opts);
        const totalCount = parseInt(res.headers.get('x-total-count') || '0', 10);
        const pageSize = parseInt(res.headers.get('x-page-size') || '0', 10);
        const hasMore = totalCount > ((page + 1) * pageSize);
        return {
            hasMore,
            items: res.payload,
            totalCount,
        };
    }
    async request(opts) {
        return (await this.requestInternal(opts)).payload;
    }
    async requestInternal(opts) {
        opts = typeof opts === 'object' ? opts : { url: opts };
        let { headers = {}, url } = opts;
        const { payload, query, method = 'get', retry } = opts;
        headers = new Headers(headers);
        if (payload) {
            headers.append('Content-Type', 'application/json; charset=utf-8');
        }
        const csrf = this.getCSRF();
        if (csrf) {
            headers.append('x-csrf', csrf);
        }
        if (query) {
            url += makeQueryString(query);
        }
        const response = await fetch(url, {
            body: payload ? JSON.stringify(payload) : undefined,
            credentials: 'same-origin',
            headers,
            method: method.toUpperCase(),
        });
        const { status, headers: responseHeader } = response;
        const body = await response.text();
        try {
            let json;
            if (body !== '') {
                json = JSON.parse(body, jsonReviver);
            }
            if (status >= 200 && status <= 299) {
                return {
                    headers: response.headers,
                    payload: json,
                    status: response.status,
                };
            }
            if (isAPIErrorResponse(json)) {
                if (this.shouldCSRFRetry(status, json) && retry !== false) {
                    return this.requestInternal({
                        ...opts,
                        retry: false,
                    });
                }
                throw new APIError(json, opts, {
                    headers: responseHeader,
                    payload,
                    status,
                });
            }
            throw new RequestError('Unexpected response format', opts, {
                headers: responseHeader,
                payload: json,
                status,
            });
        }
        catch (e) {
            if (e instanceof SyntaxError) {
                throw new RequestError('Expected JSON', opts, {
                    headers: responseHeader,
                    payload: body,
                    status,
                });
            }
            throw e;
        }
    }
    getCSRF() {
        const split = document.cookie.split(';');
        const csrf = split.find(s => s.includes('csrf'));
        if (!csrf) {
            return;
        }
        const match = csrf.match(/csrf=(.+)$/);
        if (match) {
            return match[1];
        }
        return;
    }
    shouldCSRFRetry(status, json) {
        const code = json.errors && json.errors.authentication && json.errors.authentication.code;
        return status === 401 &&
            (code === CSRFCodes.Mismatch ||
                code === CSRFCodes.MissingCookie ||
                code === CSRFCodes.MissingHeader);
    }
    xhrResponseObject(xhr) {
        return {
            headers: new Headers((xhr.getAllResponseHeaders() || '').trim().split(/[\r\n]+/)
                .map(r => {
                const [key, rawValue] = r.split(':');
                // tslint:disable-next-line: no-unnecessary-type-assertion
                return [key, rawValue.trim()];
            })),
            payload: xhr.response,
            status: xhr.status,
        };
    }
}
//# sourceMappingURL=APIClient.js.map